import { useEffect, useState } from "react";
import { Loader2, RefreshCw, ArrowRight } from "lucide-react";
import { adminApi } from "@/lib/admin";

/**
 * Failover Log — live table of the last N cross-engine auto-switches.
 * Reads from GET /api/admin/failover-log (backed by the Mongo
 * `engine_failovers` collection, 7-day TTL). Each row = one hop:
 *
 *    DeepSeek  →  Gemini    reason=ttfb          14:32:07
 *    Gemini    →  Sarvam    reason=429           14:32:08
 *    Sarvam    →  Claude    reason=network       14:32:09
 *
 * Auto-refreshes every 30s. Also shows a "top 5 paths in last 24h"
 * summary so operators can spot which engine hop dominates today.
 */

const REASON_COPY = {
  "429": "Rate limit (429)",
  "503": "Service unavailable",
  "5xx": "Server error",
  other: "Bad request / filter",
  timeout: "Timeout",
  network: "Network unreachable",
  ttfb: "No first token in time",
  stream_gap: "Stream died mid-reply",
  empty_response: "200 OK but empty",
  key_cooldown: "All keys cooling down",
  unknown: "Unknown",
};

function reasonLabel(k) {
  return REASON_COPY[k] || k || "unknown";
}

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString(undefined, {
      month: "short",
      day: "2-digit",
      hour: "2-digit",
      minute: "2-digit",
      second: "2-digit",
    });
  } catch {
    return iso;
  }
}

export default function FailoverLogSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const load = async () => {
    setLoading(true);
    setError("");
    try {
      const r = await adminApi.failoverLog(20);
      setData(r);
    } catch (err) {
      setError(err.message || "Couldn't load failover log.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const id = setInterval(load, 30_000);
    return () => clearInterval(id);
  }, []);

  if (loading && !data) {
    return (
      <p
        data-testid="failover-log-loading"
        className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] mb-3"
      >
        <Loader2 size={12} className="inline animate-spin mr-2" />
        Loading failover log…
      </p>
    );
  }
  if (error) {
    return (
      <p
        data-testid="failover-log-error"
        className="font-sans text-sm text-red-400 border-l-2 border-red-400 pl-3 mb-3"
      >
        {error}
      </p>
    );
  }
  if (!data) return null;

  const events = data.events || [];
  const top = data.top_24h || [];
  const total24h = data.total_24h || 0;

  return (
    <div className="space-y-6" data-testid="failover-log-panel">
      <div className="flex items-center justify-between gap-4 flex-wrap">
        <p className="font-sans text-sm text-[var(--text-dim)] max-w-2xl leading-relaxed">
          Every automatic engine switch is logged here. Rows appear the
          instant a fallback fires — no restart, no polling behind the
          scenes. If this table is empty, the ladder never had to walk;
          the primary engine is serving cleanly.
        </p>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="failover-log-refresh"
          className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] hover:text-[var(--text)] transition-colors inline-flex items-center gap-2"
        >
          <RefreshCw
            size={12}
            className={loading ? "animate-spin" : ""}
          />
          Refresh
        </button>
      </div>

      {/* 24h headline */}
      <div className="flex items-baseline gap-6 flex-wrap">
        <span
          data-testid="failover-log-total-24h"
          className="font-mono-ui text-3xl font-medium text-[var(--text)]"
        >
          {total24h}
        </span>
        <span className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
          Total switches in last 24h
        </span>
      </div>

      {/* Top paths in last 24h */}
      {top.length > 0 && (
        <div className="space-y-2" data-testid="failover-log-top-paths">
          <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)]">
            Top paths (24h)
          </p>
          <div className="flex flex-wrap gap-2">
            {top.map((p, i) => (
              <span
                key={`${p.from_engine}-${p.to_engine}-${i}`}
                className="font-mono-ui text-[11px] tracking-[0.08em] uppercase text-[var(--text)] border border-[var(--border)] px-3 py-1.5 rounded-sm inline-flex items-center gap-2"
              >
                <span>{p.from_engine}</span>
                <ArrowRight size={11} className="opacity-60" />
                <span>{p.to_engine}</span>
                <span className="opacity-60">·</span>
                <span className="opacity-80">{p.count}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Recent events table */}
      <div>
        <p className="font-mono-ui text-[10px] tracking-[0.22em] uppercase text-[var(--text-dim)] mb-3">
          Last {events.length} switches
        </p>
        {events.length === 0 ? (
          <p
            data-testid="failover-log-empty"
            className="font-sans text-sm text-[var(--text-dim)] italic"
          >
            No auto-switches recorded yet. The primary engine is serving
            every request without help.
          </p>
        ) : (
          <div className="border border-[var(--border)] overflow-hidden">
            <table
              data-testid="failover-log-table"
              className="w-full font-mono-ui text-[11px]"
            >
              <thead className="bg-[var(--surface-2,rgba(255,255,255,0.03))]">
                <tr className="text-left">
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">
                    When
                  </th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">
                    From
                  </th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">
                    To
                  </th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">
                    Reason
                  </th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">
                    Mid-stream
                  </th>
                  <th className="px-3 py-2 tracking-[0.18em] uppercase text-[var(--text-dim)] font-medium">
                    Role
                  </th>
                </tr>
              </thead>
              <tbody>
                {events.map((e, i) => (
                  <tr
                    key={`${e.ts}-${i}`}
                    data-testid={`failover-log-row-${i}`}
                    className="border-t border-[var(--border)]"
                  >
                    <td className="px-3 py-2 text-[var(--text-dim)] whitespace-nowrap">
                      {fmtTs(e.ts)}
                    </td>
                    <td className="px-3 py-2 text-[var(--text)]">
                      {e.from_engine}
                    </td>
                    <td className="px-3 py-2 text-[var(--text)]">
                      <ArrowRight
                        size={11}
                        className="inline mr-1 opacity-60"
                      />
                      {e.to_engine}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-dim)]">
                      {reasonLabel(e.fail_reason)}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-dim)]">
                      {e.mid_stream ? "yes" : "—"}
                    </td>
                    <td className="px-3 py-2 text-[var(--text-dim)]">
                      {e.user_role || "anonymous"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
